"use client";
import { BlockBreadcrumbsV2 } from "@/app/_blocks/BlockBreadcrumbsv2";
import { Button } from "@ui/components/core/Button";
import { SwitcherButton } from "@ui/components/core/SwitcherButton";
import { cn } from "@ui/lib/utils";
import ArrowUp from "@ui/icons/arrow_up.svg";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  MAX_UPLOAD_DURATION_MINUTES,
  MAX_UPLOAD_SIZE_BYTES,
  MAX_UPLOAD_SIZE_MB,
  TranslateAudioState,
  useHeroTranslateAudio,
} from "./useHeroTranslateMedia";
import { Check, Trash } from "lucide-react";
import { HeroTranslateMediaPlayer } from "./HeroTranslateMediaPlayer";
import { CategoryCardBody, CategoryCardRoot } from "@ui/components/core/CategoryCard";
import Download20 from "@ui/icons/20x20/download-20.svg";
import { ICON_ALIGN_OPTIONS } from "@ui/tokens/icon-types";
import { Media } from "@mw/shared/payload-types";
import { LimitReachedOverlay } from "@ui/components/demos/LimitReachedOverlay";
import { useHerotranslateMediaTranslations } from "./HeroTranslateMediaTranslations";
import FakeProgressBar from "@ui/components/core/FakeProgressBar";
import { appUrls } from "@/constants";
import { LanguageOrAccent, languages as languagesAndAccents } from "@mw/shared/languagesAndAccents";
import { LanguageSelector as CoreLanguageSelector } from "@ui/components/core/LanguageSelector";
import type { LanguageMetadata } from "@ui/components/languages/config";
import { EventPropagationBoundary } from "@ui/components/composers/EventPropagationBoundary";
import { SelectTrigger } from "@ui/components/core/Select";
import { EventName } from "@mw/shared/analyticsEventNames";
import clsx from "clsx";
import { LinkTracked } from "@ui/components/core/LinkTracked";

export interface HeroTranslateMediaClientDemoProps {
  breadcrumbs?: { href?: string; title: string }[];
  title?: null | string;
  fromLangCode?: string;
  toLangCode?: string;
  initialMode?: "audio" | "video";
  media: {
    beforeUrl: string;
    afterUrl: string;
  };
}

const sttLanguages = languagesAndAccents.filter(l => l.dubbing).sort((a, b) => a.label.localeCompare(b.label));

export default function HeroTranslateMediaClientDemo({
  breadcrumbs,
  title,
  fromLangCode = "auto",
  toLangCode,
  media,
  initialMode,
}: HeroTranslateMediaClientDemoProps) {
  const t = useHerotranslateMediaTranslations();
  const [fromLanguage, setFromLanguage] = useState<LanguageOrAccent | undefined>(
    fromLangCode && fromLangCode !== "auto" ? sttLanguages.find(l => l.value === fromLangCode) : undefined,
  );
  const [toLanguage, setToLanguage] = useState<LanguageOrAccent | undefined>(
    toLangCode ? sttLanguages.find(l => l.value === toLangCode) : undefined,
  );

  const {
    uploadVideo,
    generate,
    state,
    reset,
    selectedMediaIndex: selectedIndex,
    setSelectedMediaIndex: setSelectedIndex,
  } = useHeroTranslateAudio({
    originalVideo: {
      videoUrl: media.beforeUrl,
    },
    translatedVideo: {
      videoUrl: media.afterUrl,
    },
    initialMode,
  });

  const currentMedia =
    selectedIndex === 0 || !("translatedVideo" in state) ? state.originalVideo : state.translatedVideo;

  const selectedFile = state.type === "uploaded" || state.type === "generating" ? state.file : undefined;
  const fileThumbnail = state.type === "uploaded" || state.type === "generating" ? state.fileThumbnail : undefined;

  return (
    <>
      {/* TOP */}
      <div className='gap-24 space-y-24 lg:flex lg:space-y-0'>
        {/* Left */}
        <div className='flex flex-1 flex-col justify-between space-y-24 pr-10 lg:space-y-0'>
          <div className=''>
            {breadcrumbs && <BlockBreadcrumbsV2 links={breadcrumbs} size='small' hidenOnMobileAfter={0} />}
            <h1 className='f-heading-03 max-w-3xl lg:f-heading-02'>{title}</h1>
          </div>

          {state.type === "complete" ? (
            <CompleteCard
              downloadUrl={`${appUrls.dubbing}?claim_dubbing_id=${state.dubbing_id}`}
              fileThumbnail={state.fileThumbnail}
              state={state}
              fromLanguage={fromLanguage}
              toLanguage={toLanguage}
            />
          ) : (
            <VideoUploadDropzone
              onFilesDropped={files => {
                if (files.length === 0) return;
                uploadVideo(files[0]);
              }}
              generate={() => generate(fromLanguage?.value || "auto", toLanguage?.value || "")}
              isGenerating={state.type === "generating"}
              clearFiles={reset}
              selectedFile={selectedFile}
              fileThumbnail={fileThumbnail}
              fromLanguage={fromLanguage}
              toLanguage={toLanguage}
              onFromLanguageChange={val => setFromLanguage(sttLanguages.find(l => l.value === val))}
              onToLanguageChange={val => setToLanguage(sttLanguages.find(l => l.value === val))}
              sttLanguages={sttLanguages}
            />
          )}
        </div>

        {/* Right */}
        <div className='flex-1'>
          <HeroTranslateMediaPlayer
            src={currentMedia.videoUrl}
            mode={state.mode}
            audioColorScheme={currentMedia.color}
          />
        </div>
      </div>

      {/* BOTTOM */}
      <div className='flex gap-24'>
        {/* Left */}
        <div className='flex-1'></div>
        {/* Right */}
        <div className='flex-1'>
          <div className='relative mb-20 ml-auto flex max-w-sm px-32 pb-12'>
            <div className='flex-grow rounded-bl-xl border-b border-l border-default' />
            <div className='relative flex translate-y-1/2 justify-center bg-eggshell'>
              <SwitcherButton
                disabled={state.type !== "demo" && state.type !== "complete"}
                onSelect={newIndex => {
                  if (newIndex === selectedIndex || (state.type !== "demo" && state.type !== "complete")) return;
                  setSelectedIndex(newIndex);
                }}
                options={[
                  {
                    id: "from",
                    label: fromLanguage?.label || t("widget.switcher.before"),
                  },
                  {
                    id: "to",
                    label: toLanguage?.label || t("widget.switcher.after"),
                  },
                ]}
                selectedIndex={selectedIndex}
              />
            </div>
          </div>
        </div>
      </div>
      {state.type === "quota_exceeded" && (
        <div className='absolute inset-0 overflow-hidden rounded-xxl'>
          <LimitReachedOverlay
            title={t("widget.quota_exceeded.title")}
            description={t("widget.quota_exceeded.description")}
            onClose={reset}
            trackingConfig={{
              signUpLink: {
                eventName: EventName.mw_translate_media_limit_sign_up_clicked,
                eventProperties: {
                  limit_type: "quota",
                  translate_media_mode: state.mode,
                },
              },
              closeButton: {
                eventName: EventName.mw_translate_media_limit_closed,
                eventProperties: {
                  limit_type: "quota",
                  translate_media_mode: state.mode,
                },
              },
            }}
          />
        </div>
      )}
      {state.type === "duration_exceeded" && (
        <div className='absolute inset-0 overflow-hidden rounded-xxl'>
          <LimitReachedOverlay
            title={t("widget.duration_exceeded.title")}
            description={t("widget.duration_exceeded.description", { maxDuration: MAX_UPLOAD_DURATION_MINUTES })}
            buttonText={t("widget.duration_exceeded.button")}
            onClose={reset}
            trackingConfig={{
              signUpLink: {
                eventName: EventName.mw_translate_media_limit_sign_up_clicked,
                eventProperties: {
                  limit_type: "duration",
                  translate_media_mode: state.mode,
                  max_duration_minutes: MAX_UPLOAD_DURATION_MINUTES,
                },
              },
              closeButton: {
                eventName: EventName.mw_translate_media_limit_closed,
                eventProperties: {
                  limit_type: "duration",
                  translate_media_mode: state.mode,
                  max_duration_minutes: MAX_UPLOAD_DURATION_MINUTES,
                },
              },
            }}
          />
        </div>
      )}
    </>
  );
}

const MEDIA_CONTENT_TYPES = ["audio/*", "video/*"];

export interface VideoUploadDropzoneProps {
  onFilesDropped: (files: File[]) => void;
  onError?: (error: string) => void;
  maxSize?: number; // in bytes
  selectedFile?: File;
  fileThumbnail?: string;
  clearFiles: () => void;
  isGenerating?: boolean;
  generate: () => Promise<void>;
  // language selects
  fromLanguage: LanguageOrAccent | undefined;
  toLanguage: LanguageOrAccent | undefined;
  onFromLanguageChange: (val: string) => void;
  onToLanguageChange: (val: string) => void;
  sttLanguages: LanguageOrAccent[];
}

export function VideoUploadDropzone({
  onFilesDropped,
  onError,
  maxSize = MAX_UPLOAD_SIZE_BYTES,
  selectedFile,
  fileThumbnail,
  clearFiles,
  isGenerating,
  generate,
  fromLanguage,
  toLanguage,
  onFromLanguageChange,
  onToLanguageChange,
  sttLanguages,
}: VideoUploadDropzoneProps) {
  const t = useHerotranslateMediaTranslations();
  const handleDrop = useCallback(
    (files: File[]) => {
      onFilesDropped(files);
    },
    [onFilesDropped],
  );

  const handleDropRejected = useCallback(
    (rejected: any[]) => {
      const { file, errors } = rejected[0];
      let errorMessage = t("widget.file_validation.general_error", { file: file.name });

      if (errors.some((e: any) => e.code === "file-invalid-type")) {
        errorMessage = t("widget.file_validation.invalid_type", { file: file.name });
      } else if (errors.some((e: any) => e.code === "file-too-large")) {
        errorMessage = t("widget.file_validation.too_large", { file: file.name, maxSize: MAX_UPLOAD_SIZE_MB });
      }

      if (onError) {
        onError(errorMessage);
      }
    },
    [onError, t],
  );

  const { isDragActive, getRootProps, getInputProps, open } = useDropzone({
    multiple: false,
    accept: Object.fromEntries(MEDIA_CONTENT_TYPES.map(type => [type, []])),
    maxSize,
    onDropAccepted: handleDrop,
    onDropRejected: handleDropRejected,
    noClick: true,
    noKeyboard: true,
    disabled: isGenerating,
  });

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      {...getRootProps({
        className: cn(
          "relative rounded-inxl border shadow-lg px-24 py-24 transition-colors cursor-pointer ",
          isDragActive && "border-primary bg-neutral-dark",
          isGenerating && "pointer-events-none",
        ),
      })}
      aria-label={t("widget.dropzone.title")}
      role='button'
      tabIndex={0}
      onClick={open}
    >
      <input {...getInputProps()} className='hidden' />

      {!selectedFile && (
        <>
          <div className='mb-24 flex justify-between'>
            <div className=''>
              <p className='f-paragraph-02 mb-8'>
                {fromLanguage && toLanguage
                  ? t("widget.dropzone.title_with_languages", {
                      fromLanguage: fromLanguage.label,
                      toLanguage: toLanguage.label,
                    })
                  : t("widget.dropzone.title")}
              </p>
              <p className='f-paragraph-04 text-sm'>
                {t("widget.dropzone.subtitle", {
                  maxSize: MAX_UPLOAD_SIZE_MB,
                  maxDuration: MAX_UPLOAD_DURATION_MINUTES,
                })}
              </p>
            </div>

            {/* Upload Icon */}
            <div className='bg-transparent ml-8 cursor-pointer self-start rounded-md border p-8 shadow-lg transition-colors lg:p-12'>
              <ArrowUp className='text-primary size-20 lg:size-24' />
            </div>
          </div>
          <div className='max-w-2xl'>
            {/* Select button */}
            <Button
              onClick={e => {
                e.stopPropagation();
                open();
              }}
              disabled={isGenerating}
              size='lg'
            >
              {t("widget.buttons.select_media")}
            </Button>
          </div>
        </>
      )}
      {selectedFile && (
        <>
          <CategoryCardRoot
            variant='outline'
            onClick={e => {
              e.stopPropagation();
            }}
            className={clsx(isGenerating && "opacity-50")}
          >
            <CategoryCardBody
              title={selectedFile.name}
              description={
                <>
                  <span>{(selectedFile.size / (1000 * 1000)).toFixed(2)} MB</span>
                  <span>·</span>
                  <span>{selectedFile.name.split(".").pop()}</span>
                </>
              }
              imageProps={{
                resource: {
                  alt: selectedFile.name,
                  url: fileThumbnail || "",
                  width: 100,
                  height: 100,
                } as Media,
              }}
            />

            <Button variant='ghost' size='sm' onClick={clearFiles} disabled={isGenerating}>
              <Trash />
            </Button>
          </CategoryCardRoot>
          {isGenerating && (
            <div className='pb-12 pt-24'>
              <p className='f-paragraph-02 mb-12 text-default'>{t("widget.translation_in_progress")}</p>
              <FakeProgressBar duration={100} isComplete={false} className='mx-0 h-8' />
            </div>
          )}
          {/* Language selects */}
          {!isGenerating && (
            <EventPropagationBoundary className='max-w-2xl pt-24'>
              <div className='mb-16 grid grid-cols-1 gap-12 md:grid-cols-2'>
                <div>
                  <div className='f-paragraph-04 mb-6'>{t("widget.language_select.source_language_label")}</div>
                  {(() => {
                    const selected = sttLanguages.find(l => l.value === fromLanguage?.value);
                    return (
                      <CoreLanguageSelector
                        fullWidth
                        language={selected as unknown as LanguageMetadata | undefined}
                        setSelectedLanguage={onFromLanguageChange}
                        showDropdownArrow
                        sortedLanguages={sttLanguages as unknown as LanguageMetadata[]}
                        triggerSize='md'
                        customSelectTrigger={({ language }) => (
                          <SelectTrigger className='w-full' size='md'>
                            {language ? language.label : t("widget.language_select.source_language_detect")}
                          </SelectTrigger>
                        )}
                      />
                    );
                  })()}
                </div>
                <div>
                  <div className='f-paragraph-04 mb-6'>{t("widget.language_select.target_language_label")}</div>
                  {(() => {
                    const selected = sttLanguages.find(l => l.value === toLanguage?.value);
                    return (
                      <CoreLanguageSelector
                        fullWidth
                        language={selected as unknown as LanguageMetadata | undefined}
                        setSelectedLanguage={onToLanguageChange}
                        showDropdownArrow
                        sortedLanguages={sttLanguages as unknown as LanguageMetadata[]}
                        triggerSize='md'
                        customSelectTrigger={({ language }) => (
                          <SelectTrigger className='w-full' size='md'>
                            {language ? language.label : t("widget.language_select.target_language_placeholder")}
                          </SelectTrigger>
                        )}
                      />
                    );
                  })()}
                </div>
              </div>
            </EventPropagationBoundary>
          )}
          {!isGenerating && (
            <div className='max-w-2xl pt-8'>
              {/* Select button */}
              <Button
                onClick={e => {
                  e.stopPropagation();
                  generate();
                }}
                disabled={isGenerating || !toLanguage}
                size='lg'
              >
                <span>{t("widget.buttons.translate_media")}</span>
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CompleteCard({
  fileThumbnail,
  downloadUrl,
  state,
  fromLanguage,
  toLanguage,
}: {
  fileThumbnail?: string;
  downloadUrl: string;
  state: TranslateAudioState & {
    type: "complete";
  };
  fromLanguage?: LanguageOrAccent;
  toLanguage?: LanguageOrAccent;
}) {
  const t = useHerotranslateMediaTranslations();
  return (
    <div className='relative rounded-inxl border bg-dark px-24 py-24 shadow-lg transition-colors'>
      <div className='flex flex-col items-center justify-center gap-16'>
        {/* Thumbnail with check */}
        <div className='relative'>
          <div className='h-72 w-72 overflow-hidden rounded-lg bg-overlay-light'>
            {fileThumbnail ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                alt={t("widget.complete.thumbnail_alt")}
                className='h-full w-full object-cover'
                src={fileThumbnail}
              />
            ) : null}
          </div>
          <div className='absolute -bottom-12 -right-12 rounded-sm border border-default bg-default p-4 shadow-xs'>
            <Check className='size-20 text-default' />
          </div>
        </div>

        {/* Text */}
        <div className='pt-8'>
          <h3 className='f-heading-05 text-center'>{t("widget.complete.title")}</h3>
          <p className='f-paragraph-03 mt-6 max-w-sm text-center text-lighter'>{t("widget.complete.description")}</p>
        </div>

        <div className='mt-24 flex flex-col items-center justify-center gap-12 md:flex-row'>
          <LinkTracked
            href={downloadUrl}
            asButton
            buttonVariants={{
              size: "lg",
              variant: "primary",
            }}
            icon={<Download20 />}
            iconPosition={ICON_ALIGN_OPTIONS.BEFORE}
            eventName={EventName.mw_translate_media_download_clicked}
            eventProperties={{
              translate_media_mode: state.mode,
              source_lang: fromLanguage?.value,
              target_lang: toLanguage?.value,
            }}
          >
            {t("widget.complete.download")}
          </LinkTracked>
          <LinkTracked
            eventName={EventName.mw_translate_media_edit_clicked}
            eventProperties={{
              translate_media_mode: state.mode,
              source_lang: fromLanguage?.value,
              target_lang: toLanguage?.value,
            }}
            href={downloadUrl}
            asButton
            buttonVariants={{
              size: "lg",
              variant: "ghost",
            }}
          >
            {t("widget.complete.edit")}
          </LinkTracked>
        </div>
      </div>
    </div>
  );
}
